import {
  Button,
  ErrorMessage,
  InputGroup,
  Label,
  ProgressBar,
  Spinner,
  TextField,
} from "@heroui/react";
import { useState, type FormEvent } from "react";

import {
  EyeIcon,
  EyeOffIcon,
  LockIcon,
  MailIcon,
} from "@/src/components/icons";
import { useTranslations } from "@/src/i18n/i18n";
import { useSignIn } from "@/src/pages/SignIn/hooks/useSignIn";

/**
 * `ProgressBar` est une grille à deux lignes — « libellé / valeur » puis
 * « piste » — séparées par un `gap`. Sans libellé la première ligne est vide,
 * mais l'espace, lui, reste : la barre flotterait 4 px trop bas.
 *
 * Réglé en style *inline* parce qu'un utilitaire Tailwind ne suffirait pas :
 * le CSS de `@heroui/styles` n'est pas *layerisé* et bat donc `@layer
 * utilities`, quelle que soit la classe. Hissé hors du rendu pour ne pas créer
 * un objet neuf à chaque passage.
 */
const NO_LABEL_GAP = { gap: 0 };

/**
 * Formulaire de connexion.
 *
 * Volontairement **non contrôlé** : la valeur ne sert qu'à la soumission, un
 * `useState` par champ n'apporterait qu'un render de plus par frappe. Seule la
 * visibilité du mot de passe est un état, parce qu'elle change le rendu.
 */
export function SignInForm() {
  const { signIn, isPending, signInErrors, removeSignInError } = useSignIn();
  const [showPassword, setShowPassword] = useState(false);
  const t = useTranslations("signIn");
  const ta = useTranslations("a11y");

  const errorFor = (field: string) =>
    signInErrors?.find((error) => error.field === field)?.message;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = new FormData(event.currentTarget);
    signIn({
      email: String(form.get("email") ?? ""),
      password: String(form.get("password") ?? ""),
    });
  };

  return (
    /**
     * Attendre le serveur se dit à trois endroits, et c'est voulu : la barre
     * indéterminée en haut (« il se passe quelque chose »), les champs
     * verrouillés (« ne touche plus à rien ») et le bouton (« voilà quoi »).
     * Un seul de ces signaux passerait inaperçu.
     */
    <form
      aria-busy={isPending}
      className="flex flex-col gap-5"
      onSubmit={handleSubmit}
    >
      {/* Hauteur réservée : la barre apparaît et disparaît sans décaler le
          formulaire sous elle. */}
      <div className="h-1">
        {isPending ? (
          <ProgressBar
            aria-label={t("in-progress")}
            isIndeterminate
            size="sm"
            style={NO_LABEL_GAP}
          >
            <ProgressBar.Track>
              <ProgressBar.Fill />
            </ProgressBar.Track>
          </ProgressBar>
        ) : null}
      </div>

      <TextField
        autoComplete="email"
        isDisabled={isPending}
        isInvalid={Boolean(errorFor("email"))}
        isRequired
        name="email"
        type="email"
        onChange={() => removeSignInError("email")}
      >
        <Label>{t("email")}</Label>
        <InputGroup
          fullWidth
          // `InputGroup` ne dérive pas l'état du `TextField` qui l'entoure :
          // sans ces rappels, ni l'erreur ni le verrouillage ne se verraient.
          isDisabled={isPending}
          isInvalid={Boolean(errorFor("email"))}
        >
          <InputGroup.Prefix>
            <MailIcon className="size-4" />
          </InputGroup.Prefix>
          <InputGroup.Input placeholder="admin@wargameapp.com" />
        </InputGroup>
        {errorFor("email") ? (
          <ErrorMessage>{errorFor("email")}</ErrorMessage>
        ) : null}
      </TextField>

      <TextField
        autoComplete="current-password"
        isDisabled={isPending}
        isInvalid={Boolean(errorFor("password"))}
        isRequired
        name="password"
        type={showPassword ? "text" : "password"}
        onChange={() => removeSignInError("password")}
      >
        <Label>{t("password")}</Label>
        <InputGroup
          fullWidth
          isDisabled={isPending}
          isInvalid={Boolean(errorFor("password"))}
        >
          <InputGroup.Prefix>
            <LockIcon className="size-4" />
          </InputGroup.Prefix>
          <InputGroup.Input placeholder="••••••••" />
          <InputGroup.Suffix className="px-1">
            <Button
              aria-label={ta(showPassword ? "hide-password" : "show-password")}
              isDisabled={isPending}
              isIconOnly
              size="sm"
              // `type="button"` : sans lui, le bouton soumettrait le formulaire.
              type="button"
              variant="ghost"
              onPress={() => setShowPassword((visible) => !visible)}
            >
              {showPassword ? (
                <EyeOffIcon className="size-4" />
              ) : (
                <EyeIcon className="size-4" />
              )}
            </Button>
          </InputGroup.Suffix>
        </InputGroup>
        {errorFor("password") ? (
          <ErrorMessage>{errorFor("password")}</ErrorMessage>
        ) : null}
      </TextField>

      {/*
        Pas d'`aria-label` : le contenu nomme déjà le bouton, et il change
        pendant l'attente. Un libellé figé annoncerait « Se connecter » alors
        que la requête est déjà partie.
      */}
      <Button
        className="mt-1"
        fullWidth
        isDisabled={isPending}
        size="lg"
        type="submit"
      >
        {isPending ? (
          <>
            {/*
              ⚠️ `color="current"` est indispensable : la couleur par défaut du
              `Spinner` est `accent`, soit exactement la couleur de fond d'un
              bouton primaire. Sans ça il tourne en violet sur violet et
              l'attente ne se voit pas du tout.
            */}
            <Spinner color="current" size="sm" />
            {t("submitting")}
          </>
        ) : (
          t("submit")
        )}
      </Button>
    </form>
  );
}
